const gameEvents = new Set(['app_loaded', 'level_viewed', 'level_started', 'level_completed', 'level_failed', 'renderer_fallback']);
const teacherEvents = new Set(['teacher_page_viewed', 'teacher_lesson_viewed', 'teacher_lesson_print_requested',
  'teacher_game_launched', 'teacher_content_load_failed', 'teacher_lesson_not_found']);

export function allowedAnalyticsEvent(event, teacher) {
  return (teacher ? teacherEvents : gameEvents).has(event);
}

// Only authored metadata and aggregate counts; no program text or free-form input.
const fields = new Set(('interface_language block_language explorer screen world_id level_id level_number level_mode '
  + 'content_revision case_index previously_completed best_stars level_visit_id attempt_number execution_mode '
  + 'program_block_count attempt_id attempt_duration_ms attempt_count failed_attempts aborted_attempts hints_requested '
  + 'execution_duration_ms visit_duration_ms success result exit_reason reason moves stars new_gem first_completion '
  + 'completed_levels startup_duration_ms renderer low_resource_mode from to teacher_page site_section unit_id planned_minutes').split(' '));
export function analyticsProperties(properties = {}) {
  const clean = {};
  for (const [key, value] of Object.entries(properties)) {
    if (fields.has(key) && (typeof value === 'boolean' || (typeof value === 'number' && Number.isFinite(value))
      || (typeof value === 'string' && value.length <= 100))) clean[key] = value;
  }
  for (const key of ['failure_reasons', 'abort_reasons']) {
    if (properties[key] && typeof properties[key] === 'object')
      clean[key] = Object.fromEntries(Object.entries(properties[key]).filter(([reason, count]) =>
        /^[a-zA-Z_:]{1,60}$/.test(reason) && Number.isInteger(count) && count >= 0).slice(0, 40));
  }
  return { ...clean, analytics_version: 3 };
}

// Apply at the SDK transport boundary, including automatically generated events.
// The budget is per document load, shared by handled and unhandled exceptions.
export function createPosthogPolicy({ teacher, maxExceptions = 3 }) {
  const exceptions = new Set();
  let pageviewCaptured = false;
  return event => {
    if (!event) return null;
    if (event.event === '$pageview') {
      if (pageviewCaptured) return null;
      pageviewCaptured = true;
      event.properties = { ...event.properties, analytics_version: 3 };
      return event;
    }
    if (event.event === 'survey sent') return teacher ? event : null;
    if (allowedAnalyticsEvent(event.event, teacher)) {
      // A successfully rendered lesson has its own richer view event.
      if (event.event === 'teacher_page_viewed' && event.properties?.teacher_page === 'lesson') return null;
      const sdk = Object.fromEntries(Object.entries(event.properties ?? {}).filter(([key]) =>
        key.startsWith('$') || key === 'distinct_id' || key === 'token'));
      event.properties = { ...sdk, ...analyticsProperties(event.properties) };
      return event;
    }
    if (event.event !== '$exception' || exceptions.size >= maxExceptions) return null;
    // Use the original exception, not changing game state or diagnostic steps,
    // so a repeated error (including caught + automatic capture) uploads once.
    const signature = JSON.stringify((event.properties?.$exception_list || []).map(exception => ({
      type: exception.type,
      value: exception.value,
      frames: exception.stacktrace?.frames,
    })));
    if (exceptions.has(signature)) return null;
    exceptions.add(signature);
    return event;
  };
}
